import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/modules/auth'

const routes = [
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/Home.vue'),
        meta: { title: 'Home | LapStuff - Premium Laptop Parts & Accessories', description: 'Premium laptop parts and accessories for all major brands.' }
      },
      {
        path: 'shop',
        name: 'Shop',
        component: () => import('@/views/Shop.vue'),
        meta: { title: 'Shop All | LapStuff', description: 'Browse our extensive catalog of genuine laptop parts.' }
      },
      {
        path: 'product/:slug',
        name: 'ProductDetail',
        component: () => import('@/views/ProductDetail.vue'),
        meta: { title: 'Product Details | LapStuff', description: 'View detailed specifications and compatibility for this product.' }
      },
      {
        path: 'categories',
        name: 'Categories',
        component: () => import('@/views/Categories.vue'),
        meta: { title: 'Categories | LapStuff', description: 'Shop laptop parts by category.' }
      },
      {
        path: 'about',
        name: 'About',
        component: () => import('@/views/About.vue'),
        meta: { title: 'About Us | LapStuff', description: 'Learn more about LapStuff and our commitment to quality.' }
      },
      {
        path: 'contact',
        name: 'Contact',
        component: () => import('@/views/Contact.vue'),
        meta: { title: 'Contact Us | LapStuff', description: 'Get in touch with our customer support team.' }
      },
      {
        path: 'faq',
        name: 'FAQ',
        component: () => import('@/views/FAQ.vue'),
        meta: { title: 'FAQ | LapStuff', description: 'Frequently asked questions about our products and services.' }
      },
      {
        path: 'warranty',
        name: 'Warranty',
        component: () => import('@/views/Warranty.vue'),
        meta: { title: 'Warranty Policy | LapStuff', description: 'Information about our lifetime warranty.' }
      },
      {
        path: 'returns',
        name: 'Returns',
        component: () => import('@/views/Returns.vue'),
        meta: { title: 'Returns & Refunds | LapStuff', description: 'Read our returns and refunds policy.' }
      },
      {
        path: 'checkout',
        name: 'Checkout',
        component: () => import('@/views/Checkout.vue'),
        meta: { title: 'Checkout | LapStuff', description: 'Securely checkout your items.' }
      },
      {
        path: 'shipping',
        name: 'Shipping',
        component: () => import('@/views/Shipping.vue'),
        meta: { title: 'Shipping Info | LapStuff', description: 'Learn about our shipping and delivery processes.' }
      },
      {
        path: 'privacy',
        name: 'Privacy',
        component: () => import('@/views/Privacy.vue'),
        meta: { title: 'Privacy Policy | LapStuff', description: 'How we protect your data.' }
      },
      {
        path: 'terms',
        name: 'Terms',
        component: () => import('@/views/Terms.vue'),
        meta: { title: 'Terms of Service | LapStuff', description: 'Our terms and conditions.' }
      },
      {
        path: 'blog',
        name: 'Blog',
        component: () => import('@/views/Blog.vue'),
        meta: { title: 'Blog | LapStuff', description: 'Latest news and guides for laptop repairs.' }
      },
      {
        path: 'blog/:slug',
        name: 'BlogDetail',
        component: () => import('@/views/BlogDetail.vue'),
        meta: { title: 'Read Article | LapStuff', description: 'Read our latest blog article.' }
      },
      {
        path: 'track-order',
        name: 'TrackOrder',
        component: () => import('@/views/TrackOrder.vue'),
        meta: { title: 'Track Order | LapStuff', description: 'Track the status of your order.' }
      },
      {
        path: 'support',
        name: 'Support',
        component: () => import('@/views/Support.vue'),
        meta: { title: 'Support | LapStuff', description: 'Get help with your orders and products.' }
      },
    ],
  },
  // Auth
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { guest: true, title: 'Login | LapStuff', description: 'Login to your LapStuff account.' },
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/Register.vue'),
    meta: { guest: true, title: 'Register | LapStuff', description: 'Create a new LapStuff account.' },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: { guest: true, title: 'Forgot Password | LapStuff' },
  },
  // User Dashboard
  {
    path: '/dashboard',
    component: () => import('@/layouts/UserDashboard.vue'),
    meta: { requiresAuth: true, title: 'Dashboard | LapStuff' },
    children: [
      {
        path: '',
        name: 'UserDashboard',
        component: () => import('@/views/user/Dashboard.vue'),
        meta: { title: 'My Dashboard | LapStuff' }
      },
      {
        path: 'orders',
        name: 'UserOrders',
        component: () => import('@/views/user/Orders.vue'),
        meta: { title: 'My Orders | LapStuff' }
      },
      {
        path: 'orders/:id',
        name: 'UserOrderDetail',
        component: () => import('@/views/user/OrderDetail.vue'),
        meta: { title: 'Order Details | LapStuff' }
      },
      {
        path: 'wishlist',
        name: 'UserWishlist',
        component: () => import('@/views/user/Wishlist.vue'),
        meta: { title: 'My Wishlist | LapStuff' }
      },
      {
        path: 'addresses',
        name: 'UserAddresses',
        component: () => import('@/views/user/Addresses.vue'),
        meta: { title: 'My Addresses | LapStuff' }
      },
    ],
  },
  // Admin
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin Dashboard | LapStuff' },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
      },
      {
        path: 'products',
        name: 'AdminProducts',
        component: () => import('@/views/admin/Products.vue'),
      },
      {
        path: 'orders',
        name: 'AdminOrders',
        component: () => import('@/views/admin/Orders.vue'),
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('@/views/admin/Categories.vue'),
      },
      {
        path: 'customers',
        name: 'AdminCustomers',
        component: () => import('@/views/admin/Customers.vue'),
      },
      {
        path: 'analytics',
        name: 'AdminAnalytics',
        component: () => import('@/views/admin/Analytics.vue'),
      },
      {
        path: 'coupons',
        name: 'AdminCoupons',
        component: () => import('@/views/admin/Coupons.vue'),
      },
      {
        path: 'reviews',
        name: 'AdminReviews',
        component: () => import('@/views/admin/Reviews.vue'),
      },
      {
        path: 'banners',
        name: 'AdminBanners',
        component: () => import('@/views/admin/Banners.vue'),
      },
      {
        path: 'blog-posts',
        name: 'AdminBlogPosts',
        component: () => import('@/views/admin/BlogPosts.vue'),
      },
      {
        path: 'faqs',
        name: 'AdminFaqs',
        component: () => import('@/views/admin/Faqs.vue'),
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@/views/admin/Settings.vue'),
      },
    ],
  },
  // Admin Login
  {
    path: '/admin/login',
    name: 'AdminLogin',
    component: () => import('@/views/AdminLogin.vue'),
    meta: { guest: true, title: 'Admin Login | LapStuff' },
  },
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: 'Page Not Found | LapStuff' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  // 1. Guest-only pages (login, register, admin-login)
  if (to.meta.guest && authStore.isAuthenticated) {
    return next(authStore.isAdmin ? '/admin' : '/')
  }

  // 2. Admin routes (skip admin login page itself)
  if (to.path.startsWith('/admin') && to.path !== '/admin/login') {
    // Unauthenticated -> admin login
    if (!authStore.isAuthenticated) {
      return next('/admin/login')
    }
    // Authenticated but not admin -> home
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      return next('/')
    }
  }

  // 3. Other auth-required routes
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next('/login')
  }

  next()
})

router.afterEach((to) => {
  if (to.meta.title) {
    document.title = to.meta.title
  } else {
    document.title = 'LapStuff - Premium Laptop Parts & Accessories'
  }

  if (to.meta.description) {
    let metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', to.meta.description)
    }
  }
})

export default router

